import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Card, CardContent, Button, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  submitLoginForm,
  validateResetPassHash,
  isForcePasswordActiveForTheUser,
} from 'reducers/UserReducer';
import projectLogo from 'assets/images/icon-CAD.svg';
import brandIcon from 'assets/images/brand-icon.png';
import ptsLogo from 'assets/images/PTS-logo.png';
import splashScreen from 'assets/images/login-splash.jpg';
import { checkForUpdates } from 'reducers/AppInfoReducer';
import { validateEmail } from 'utils/functions';
import TextField2 from 'components/TextField2';
import { notify } from 'reducers/NotifierReducer';
import { sendPassResetLink, setSecondFactor } from 'reducers/UserReducer';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  pageWrap: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundImage: `url(${splashScreen})`,
    backgroundSize: 'cover',
  },
  card: {
    width: 450,
    maxWidth: '95%',
    background: '#fff',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    '& img': {
      width: '100%',
    },
    '& > h1': {
      textAlign: 'center',
      textTransform: 'uppercase',
      fontSize: 28,
      padding: '8px 0',
      marginBottom: 0,
      background: '#000',
      color: '#000',
    },
  },
  content: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  branding: {
    width: '100%',
    height: 200,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#000',
    '& img': {
      width: 150,
      height: 'auto',
    },
  },
  branding2: {
    width: '100%',
    textAlign: 'center',
    padding: '45px 0 5px',
    '& img': {
      height: 50,
      width: 'auto',
    },
  },
  formWrap: {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
  },
  actions: {
    textAlign: 'center',
    marginTop: 30,
    '& .customBtn': {
      background: '#000',
      '&:hover': {
        background: '#555',
      },
    },
    '& button': {
      paddingLeft: '4em',
      paddingRight: '4em',
      textTransform: 'uppercase',
    },
    '& button.Mui-disabled': {
      color: 'rgba(0, 0, 0, 0.26)',
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
    },
  },
  input: {
    color: '#333 !important',
    '& input': {
      boxShadow: 'none !important',
      color: '#333 !important',
      textFillColor: '#333 !important',
    },
  },
  version: {
    position: 'absolute',
    bottom: '0.5em',
    left: '1em',
    lineHeight: 1,
  },
  passReset: {
    textAlign: 'right',
    '& span': {
      fontSize: 13,
      color: theme.colors.grey3,
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  passResetDisabled: {
    pointerEvents: 'none',
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  codeText: {
    color: '#0153a3',
    marginBottom: '2px',
    display: 'block',
  },
  textField: {
    width: '100%',
  },
}));

const wrkKey = window.__RUNTIME_CONFIG__.REACT_APP_WORSTATION_KEY;

const PageLogin = props => {
  const classes = useStyles();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [key, setKey] = useState('');
  const [wrkPassed, setWrkPassed] = useState(!wrkKey);
  const [view, setView] = useState('login'); // login, pass-reset
  const [forcePasswordReset, setForcePasswordReset] = useState(false);
  const {appInfo, secondFactor, setSecondFactor } = props;
  const [secondFactorToken, setSecondFactorToken] = useState('');
  const { appVersion } = appInfo;
  const [code, setCode] = useState(null);
  const history = useHistory();
  useEffect(() => {
    props.checkForUpdates();
    checkWrkPass();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!wrkKey) return;
    if (key === wrkKey) {
      window.localStorage.setItem('wrk-ps', window.btoa(key));
      window.location.reload();
    }
  }, [key]);

  useEffect(() => {
    setSecondFactor(false);
  }, []);

  useEffect(() => {
    if (secondFactor === true) setView('login-second-factor');
    if (secondFactor === false) setView('login');
  }, [secondFactor]);

  const checkWrkPass = () => {
    const savedWrkKey = window.atob(window.localStorage.getItem('wrk-ps'));
    if (savedWrkKey === wrkKey && !wrkPassed) setWrkPassed(true);
  };

  const handleUsernameChange = event => {
    setUsername(event.target.value);
  };

  const handlePasswordChange = event => {
    setPassword(event.target.value);
  };

  const handleCodeChange = (event) => setCode(event.target.value);

  const clickCancel = () => {
    setCode(null);
    setUsername('');
    setView('login');
    setSecondFactor(false);
    setForcePasswordReset(false);
  };

  const handleSecondFactorTokenChange = (event) => {
    setSecondFactorToken(event.target.value);
  };

  const submitForm = async event => {
    event.preventDefault();
    const forcePasswordReset = await isForcePasswordActiveForTheUser(username);
    if (!forcePasswordReset) {
      props.submitLoginForm(username, password);
    } else {
      setForcePasswordReset(true);
      await passResetClick(false);
    }
  };

  const passResetSubmit = async (event) => {
    event.preventDefault();
    try {
      const valid = await validateResetPassHash(code, username);
      if (valid.status === 'expired') {
        props.notify('This code has expired. Please click resend code for a new code');
      } else if (valid.status === 'invalid') {
        props.notify('The code you entered is invalid. Please check code and try again');
      } else if (valid.status === 'valid') {
        history.push('/Reset-password', { user: username, code });
      }
    } catch (err) {
      console.log(err);
    }
  };

  const secondFactorSubmit = async (event) => {
    event.preventDefault();
    props.submitLoginForm(username, password, secondFactorToken);
  };

  const passResetClick = async (isResendRequest = false) => {
    try {
      const response = await sendPassResetLink(username, isResendRequest);
      if (!response || response === 'disabled') {
        setView('login');
        props.notify(
          response === 'disabled'
            ? 'User is disabled. Please contact system administrator.'
            : 'Username does not exist. Please enter a valid username.',
          response === 'disabled' ? 'error' : 'info'
        );
      } else {
        if (!isResendRequest) {
          setEmail(response);
          setView('pass-reset');
        } else {
          props.notify(
            'Your verification code has been resent. Please check your email.',
            'success'
          );
        }
      }
    } catch (err) {
      console.log(err);
      props.notify('Error sending password reset code', 'error');
    }
  };

  const renderLoginForm = () => (
    <form onSubmit={submitForm}>
      <div className={classes.formWrap}>
        <TextField
          required
          variant="outlined"
          label="Username"
          fullWidth
          placeholder="Enter your username"
          type="text"
          value={username}
          onChange={handleUsernameChange}
          autoComplete="username"
          size="small"
          InputProps={{
            className: classes.input,
          }}
        />
      </div>
      <div className={classes.formWrap}>
        <TextField
          required
          variant="outlined"
          label="Password"
          fullWidth
          placeholder="Enter your password"
          type="password"
          value={password}
          onChange={handlePasswordChange}
          autoComplete="current-password"
          size="small"
          InputProps={{
            className: classes.input,
          }}
        />
      </div>
      <div className={classes.passReset}>
        <span
          className={!username ? classes.passResetDisabled : ''}
          onClick={() => passResetClick(false)}>
          Password reset
        </span>
      </div>
      <div className={classes.actions}>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          size="large"
          className="customBtn">
          Login
        </Button>
      </div>
    </form>
  );

  const renderPassReset = () => (
    <form onSubmit={passResetSubmit}>
      <div className={classes.formWrap}>
        <TextField2
          required
          variant="outlined"
          label="Username"
          placeholder="Enter your username"
          type="text"
          value={username}
          onChange={handleUsernameChange}
          className={classes.textField}
          inputProps={{ readOnly: true }}
        />
      </div>

      {!code && (
        <span className={`${classes.codeText} px-1 d`}>Check email for code sent to {email}</span>
      )}

      <div className={`${classes.formWrap} mt-2`}>
        <TextField2
          type="number"
          required
          variant="outlined"
          label="Code"
          placeholder="Enter code here"
          value={code}
          onChange={handleCodeChange}
          className={classes.textField}
        />
      </div>

      <div className={classes.passReset}>
        <span onClick={() => passResetClick(true)}>Resend code</span>
      </div>

      <div className={classes.actions}>
        <div className="d-flex justify-content-between">
          <Button type="button" variant="outlined" size="large" onClick={clickCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            size="large"
            disabled={!code}
            className="customBtn">
            Continue
          </Button>
        </div>
      </div>
    </form>
  );

  const renderSecondFactor = () => {
    return (
      <form onSubmit={secondFactorSubmit}>
        <div className={classes.formWrap}>
          <TextField2
            required
            variant="outlined"
            label="Token"
            fullWidth
            placeholder="Enter token"
            type="text"
            size="small"
            value={secondFactorToken}
            onChange={handleSecondFactorTokenChange}
            className={classes.textField}
          />
        </div>
        <div className={classes.actions}>
          <div className="d-flex justify-content-between">
            <Button type="button" variant="outlined" size="large" onClick={clickCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              size="large"
              className="customBtn">
              Login
            </Button>
          </div>
        </div>
      </form>
    );
  };

  return (
    <div className={classes.pageWrap}>
      <Card className={classes.card}>
        {/* <h1>. </h1> */}
        <div className={classes.branding}>
          <img src={brandIcon} alt="Setting icon" />
        </div>
        <div className={classes.branding2}>
          <img src={ptsLogo} alt="PTS logo" />
        </div>
        {forcePasswordReset && view === 'pass-reset' && (
          <div style={{ color: 'red', textAlign: 'center', marginTop: '7px' }}>
            For security reasons, you must change your password.
          </div>
        )}
        <CardContent className={classes.content}>
          {!wrkPassed && (
            <div className={classes.formWrap}>
              <TextField
                variant="outlined"
                label="Workstation Key"
                fullWidth
                placeholder="Enter your Workstation Key"
                type="password"
                value={key}
                onChange={ev => setKey(ev.target.value)}
                autoComplete="new-password"
                size="small"
                InputProps={{
                  className: classes.input,
                  autoComplete: 'new-password',
                }}
              />
            </div>
          )}
          {wrkPassed && (
            <>
              {view === 'login' && renderLoginForm()}
              {view === 'login-second-factor' && renderSecondFactor()}
              {view === 'pass-reset' && renderPassReset()}
            </>
          )}
          <div className={classes.version}>{appVersion}</div>
        </CardContent>
      </Card>
    </div>
  );
};

const mapStateToProps = state => ({
  themeMode: state.theme.mode,
  appInfo: state.appInfo,
  secondFactor: state.user.secondFactor,
});

export default connect(mapStateToProps, {
  submitLoginForm,
  checkForUpdates,
  notify,
  setSecondFactor,
  isForcePasswordActiveForTheUser,
})(PageLogin);
